import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import {
  useFirestore,
  useFirestoreModule,
  useFunctions,
  useFunctionsModule,
} from "../../data/FirebaseContext";
import moment from "moment-timezone";

const GiftcardListItem = ({ row }) => {
  const firestore = useFirestoreModule();
  const db = useFirestore();
  const functions = useFunctions();
  const functionsModule = useFunctionsModule();

  const [sessionData, setSessionData] = useState({});
  const [fikenPostings, setFikenPostings] = useState([]);
  const [orderPending, setOrderPending] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (!firestore || !row.code) return;

    const sessionsListener = firestore.onSnapshot(
      firestore.query(
        firestore.collection(db, "sessions"),
        firestore.where("code", "==", row.code),
      ),
      (snaps) => {
        if (snaps.docs.length === 1) {
          setSessionData({ id: snaps.docs[0].id, ...snaps.docs[0].data() });
        }
      },
    );

    const fikenListener = firestore.onSnapshot(
      firestore.collection(db, `giftcards/${row.code}/fikenPostings`),
      (snaps) => {
        const postings = snaps.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setFikenPostings(postings);
      },
    );

    return () => {
      sessionsListener();
      fikenListener();
    };
  }, [firestore, db, row]);

  const handleCopy = useCallback(async (value, fieldKey) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldKey);
      setTimeout(() => {
        setCopiedField((current) => (current === fieldKey ? null : current));
      }, 900);
    } catch (err) {
      console.error("Failed to copy", err);
    }
  }, []);

  const copyClassName = useCallback(
    (fieldKey) => {
      return `list-cell list-copy ${copiedField === fieldKey ? "is-copied" : ""}`;
    },
    [copiedField],
  );

  const registerSent = () => {
    firestore.updateDoc(firestore.doc(db, `giftcards/${row.code}`), {
      sent: true,
    });
  };

  const handleOrderShipping = useCallback(async () => {
    if (!functions || !functionsModule || !row?.code || orderPending) return;

    setOrderPending(true);
    setOrderError(null);

    try {
      const orderShipping = functionsModule.httpsCallable(
        functions,
        "orderShipping",
      );

      const searchParams = new URLSearchParams(window.location.search);
      const testMode = searchParams.get("testMode");

      const res = await orderShipping({
        giftCardCode: row.code,
        testMode: testMode === "true",
      });
      console.log("Shipping order response:", res);
      const data = res.data;

      if (!data.success) {
        setOrderError(data.message || "Unknown error");
      }
    } catch (err) {
      console.error(err);
      setOrderError("Could not order shipping.");
    } finally {
      setOrderPending(false);
    }
  }, [functions, functionsModule, row, orderPending]);

  const handleOpenLabel = useCallback(() => {
    if (!row?.bringLabelUrl) return;
    window.open(row.bringLabelUrl, "_blank", "noopener,noreferrer");
  }, [row]);

  const isLabelError = row?.bringLabelUrl === "error";
  const shippingButtonLabel = isLabelError
    ? "Error"
    : row?.bringLabelUrl
      ? "Print Label"
      : "Order Shipping";
  const isPrintLabel = Boolean(row?.bringLabelUrl) && !isLabelError;
  const nameValue = sessionData?.customerData?.name ?? " - ";
  const codeValue = row.code;
  const sessionIdValue = sessionData?.id ?? " - ";
  const addressLine1 = sessionData?.customerData?.address?.line1 ?? " - ";
  const addressLine2 = sessionData?.customerData?.address?.line2 ?? "";
  const addressCity = sessionData?.customerData?.address?.city ?? " - ";
  const addressPostal =
    sessionData?.customerData?.address?.postal_code ?? " - ";
  const addressValue = `${addressLine1}${addressLine2 ? `\n${addressLine2}` : ""}\n${addressPostal} ${addressCity}`;

  const anyFikenSuccess =
    fikenPostings &&
    fikenPostings.some(
      (posting) => posting.success && posting?.response?.status !== 400,
    );
  const formattedDate = moment
    .unix(row.purchaseConfirmationTs)
    .tz("Europe/Oslo")
    .format("YYYY.MM.DD");
  const formattedTime = moment
    .unix(row.purchaseConfirmationTs)
    .tz("Europe/Oslo")
    .format("HH:mm");
  const [rebookInProgress, setRebookInProgress] = useState(false);
  const rebook = useCallback(async () => {
    if (anyFikenSuccess || rebookInProgress) return;
    setRebookInProgress(true);

    const cardDoc = firestore.doc(db, `giftcards/${row.code}`);
    await firestore.updateDoc(cardDoc, {
      purchaseConfirmed: false,
    });
    await firestore.updateDoc(cardDoc, {
      purchaseConfirmed: true,
    });
    setRebookInProgress(false);
  }, [firestore, db, row, fikenPostings, anyFikenSuccess, rebookInProgress]);

  const handleDateClick = useCallback(() => {
    console.log("Gift card data:", {
      giftcard: row,
      sessionData,
    });
    handleCopy(formattedDate, "date");
  }, [row, sessionData, handleCopy, formattedDate]);

  return (
    <div className={"list-row"}>
      <div className={"list-cell"}>
        <span className={"list-cell-label"}>Date</span>
        <span
          className={`list-inline-copy ${copiedField === "date" ? "is-copied" : ""}`}
          onClick={handleDateClick}
          role={"button"}
          tabIndex={0}
        >
          {formattedDate}
        </span>
        <span
          className={`list-subtle list-inline-copy ${copiedField === "time" ? "is-copied" : ""}`}
          onClick={() => handleCopy(formattedTime, "time")}
          role={"button"}
          tabIndex={0}
        >
          {formattedTime}
        </span>
      </div>
      <div className={"list-cell"}>
        <span className={"list-cell-label"}>Name</span>
        <span
          className={`list-inline-copy ${copiedField === "name" ? "is-copied" : ""}`}
          onClick={() => handleCopy(nameValue, "name")}
          role={"button"}
          tabIndex={0}
        >
          {nameValue}
        </span>
        <span
          className={`list-subtle list-inline-copy ${copiedField === "email" ? "is-copied" : ""}`}
          onClick={(event) => {
            handleCopy(sessionData?.customerData?.email ?? " - ", "email");
          }}
          role={"button"}
          tabIndex={0}
        >
          {sessionData?.customerData?.email ?? " - "}
        </span>
      </div>
      <div className={"list-cell"}>
        <span className={"list-cell-label"}>Code</span>
        <span
          className={`list-inline-copy ${copiedField === "code" ? "is-copied" : ""}`}
          onClick={() => handleCopy(codeValue, "code")}
          role={"button"}
          tabIndex={0}
        >
          {codeValue}
        </span>
        <span
          className={`list-subtle list-inline-copy ${copiedField === "session" ? "is-copied" : ""}`}
          onClick={() => handleCopy(sessionIdValue, "session")}
          role={"button"}
          tabIndex={0}
        >
          {sessionIdValue}
        </span>
        <span
          className={`list-subtle list-inline-copy ${copiedField === "product" ? "is-copied" : ""}`}
          onClick={() => handleCopy(sessionData?.productId ?? " - ", "product")}
          role={"button"}
          tabIndex={0}
        >
          {sessionData?.productId ?? " - "}
          {sessionData?.productId === "belop"
            ? " - " +
              ((row?.reservationRequestData?.price ?? 0) / 100)
                .toFixed(0)
                .toString()
            : null}
        </span>
      </div>
      <div
        className={copyClassName("address")}
        onClick={() => handleCopy(addressValue, "address")}
        role={"button"}
        tabIndex={0}
      >
        <span className={"list-cell-label"}>Address</span>
        <span>{addressLine1}</span>
        {Boolean(addressLine2) ? <span>{addressLine2}</span> : null}
        <span>
          {addressPostal}
          {` `}
          {addressCity}
        </span>
      </div>
      <div className={"list-cell list-actions"}>
        <span className={"list-cell-label"}>Shipping</span>
        <Button
          variant={"outlined"}
          size={"small"}
          onClick={row?.bringLabelUrl ? handleOpenLabel : handleOrderShipping}
          disabled={orderPending || isLabelError}
          color={isPrintLabel ? "success" : "primary"}
        >
          {orderPending ? <CircularProgress size={16} /> : shippingButtonLabel}
        </Button>
        {orderError ? <span className={"list-error"}>{orderError}</span> : null}
      </div>
      <div className={"list-cell"}>
        <span className={"list-cell-label"}>Fiken</span>
        <div className={"list-fiken"}>
          {fikenPostings.map((posting, index) => {
            return (
              <div
                className={"list-fiken-item"}
                onClick={() => {
                  console.log(posting);
                }}
                key={index}
              >
                <Typography variant={"caption"}>
                  [{index}]:{" "}
                  {posting.success && posting?.response?.status !== 400
                    ? "Success"
                    : "Failure"}
                </Typography>
              </div>
            );
          })}
          <div>
            {!anyFikenSuccess && fikenPostings.length > 0 ? (
              <Button
                variant={"text"}
                size={"small"}
                onClick={rebook}
                disabled={rebookInProgress}
              >
                {rebookInProgress ? <CircularProgress size={16} /> : "Rebook"}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <div className={"list-cell list-actions list-actions-compact"}>
        <span className={"list-cell-label"}>Sent</span>
        {row.sent ? (
          <svg
            className={"list-icon-success"}
            viewBox={"0 0 24 24"}
            aria-hidden={"true"}
          >
            <path
              d={
                "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14l-4-4 1.41-1.41L11 13.17l4.59-4.59L17 10l-6 6z"
              }
            />
          </svg>
        ) : (
          <IconButton
            size={"small"}
            onClick={registerSent}
            aria-label={"Register sent"}
          >
            <svg
              className={"list-icon-warning"}
              viewBox={"0 0 24 24"}
              aria-hidden={"true"}
            >
              <path d={"M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"} />
            </svg>
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default GiftcardListItem;
